import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  tabName?: string;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

export default class AdminTabErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  handleRetry = () => {
    this.setState({ hasError: false, errorMessage: '' });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-950/40 border border-red-800/30 flex items-center justify-center mb-5">
            <i className="ri-error-warning-line text-red-400 text-2xl" />
          </div>
          <h3 className="font-semibold text-white text-base mb-2">
            {this.props.tabName ? `${this.props.tabName} failed to load` : 'Something went wrong'}
          </h3>
          <p className="text-teal-600 text-sm max-w-sm mb-2">
            An unexpected error occurred while rendering this section. This is not a network issue — try refreshing or clicking retry.
          </p>
          {this.state.errorMessage && (
            <p className="text-red-700 text-xs font-mono bg-red-950/30 border border-red-900/30 rounded-lg px-4 py-2 max-w-sm mb-6 break-all">
              {this.state.errorMessage}
            </p>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={this.handleRetry}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
              style={{ background: 'var(--primary)' }}
            >
              <i className="ri-refresh-line" />
              Retry
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-teal-400 border border-teal-900/40 hover:bg-teal-900/20 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-restart-line" />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
